"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";

import { PUBLIC_LIST_CTA } from "@soonlist/validators";

import { Bookmark, Share } from "~/components/icons";
import { env } from "~/env";
import { capturePublicPageCta } from "~/lib/publicPageAnalytics";
import { createOneLink } from "~/lib/urlScheme";

interface EventData {
  name?: string;
  description?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  location?: string;
}

interface SaveButtonProps {
  eventId: string;
  event?: EventData;
  type?: "button" | "icon";
  className?: string;
  userId?: string;
  eventUserId?: string;
  isSaved?: boolean;
}

function isMobile(): boolean {
  if (typeof window === "undefined") return false;

  return (
    /Android|iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function SaveButton({
  eventId,
  event,
  type = "button",
  className = "",
  userId,
  eventUserId,
  isSaved = false,
}: SaveButtonProps) {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const pathname = usePathname();
  const posthog = usePostHog();

  useEffect(() => {
    setIsMobileDevice(isMobile());
  }, []);

  const isOwner = userId && eventUserId && userId === eventUserId;

  const handleSaveClick = () => {
    capturePublicPageCta(posthog, {
      cta: PUBLIC_LIST_CTA.SAVE_EVENT,
      pathname,
      eventId,
    });
    const appUrl = createOneLink({
      deepLinkValue: "event",
      deepLinkSub1: eventId,
      campaign: "save_event",
    });
    window.open(appUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareClick = async () => {
    if (!event) return;

    const isAllDay = event.startTime && event.endTime ? false : true;
    const shareText = isAllDay
      ? `(${event.startDate || ""}, ${event.location || ""}) ${event.description || ""}`
      : `(${event.startDate || ""} ${event.startTime || ""}-${event.endTime || ""}, ${event.location || ""}) ${event.description || ""}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${event.name || "Event"} | Soonlist`,
          text: shareText,
          url: `https://${env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL}/event/${eventId}`,
        });
      } catch {
        await handleClipboardShare();
      }
    } else {
      await handleClipboardShare();
    }
  };

  const handleClipboardShare = async () => {
    try {
      await navigator.clipboard.writeText(
        `https://${env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL}/event/${eventId}`,
      );
      toast("Event URL copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error(
        "Failed to copy URL to clipboard. Please try again or copy the URL manually.",
      );
    }
  };

  const shouldShowShare = isOwner || isSaved || !isMobileDevice;

  const handleClick = shouldShowShare ? handleShareClick : handleSaveClick;
  const icon = shouldShowShare ? Share : Bookmark;
  const label = shouldShowShare ? "Share" : "Save to Soonlist";
  const text = shouldShowShare ? "Share" : "Save";

  if (type === "icon") {
    const IconComponent = icon;
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`rounded-full bg-interactive-2 p-3 ${className}`}
        aria-label={label}
      >
        <IconComponent className="size-4 text-interactive-1" />
      </button>
    );
  }

  const IconComponent = icon;
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 bg-interactive-2 px-3 py-2 ${className}`}
      style={{ borderRadius: 14 }}
      aria-label={label}
    >
      <IconComponent className="size-4 text-interactive-1" />
      <span className="text-sm font-bold text-interactive-1">{text}</span>
    </button>
  );
}
